import { useCallback, useEffect, useMemo, useRef } from "react"
import { isEqual } from "lodash-es"
import { makeStyles } from "tss-react/mui"
import { useField } from "formik"
import { Stack, Tooltip, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons"

import { CreateOrderDialogType, ExtraServiceDetails, ProductWithQuantity } from "../../../createOrderTypes"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { DeliveryTimesField, DynamicConfigKey } from "../../form/formTypes"
import { toDinero } from "../../../../../../modules/currency/currencyLib"
import { useMoney } from "../../../../../../modules/currency/useMoney"
import { StandardButton } from "../../../../buttons/StandardButton"
import { useCreateOrderDialogType } from "../../../hooks/useCreateOrderDialogType"
import { calculateServicePrice, isServiceAdded } from "./helpers"
import { ExtraServicesAccordion } from "./ExtraServicesAccordion"

const useStyles = makeStyles()((theme) => ({
    icon: {
        color: theme.palette.tailwind.slate[400],
    },
    wrap: {
        pointerEvents: "auto",
    },
}))

type ExtraServicesDeliveryAccordionProps = Readonly<{
    services: ExtraServiceDetails[]
    dialogType: CreateOrderDialogType
    deliveryTime: string
    deliveryIndex: number
}>

export function ExtraServicesDeliveryAccordion({
    services,
    dialogType,
    deliveryTime,
    deliveryIndex,
}: ExtraServicesDeliveryAccordionProps) {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})

    const { isRefundOrder } = useCreateOrderDialogType(dialogType)

    const [{ value: deliveries }, , deliveriesHelpers] = useField<DeliveryTimesField>(
        DynamicConfigKey.MULTIPLE_DELIVERIES,
    )
    const [{ value: seatCountValue }] = useField(DynamicConfigKey.NUMBER_OF_SEATS)

    const seatCount = seatCountValue || 0

    // Stable ref for deliveriesHelpers to avoid triggering effects on every render
    const deliveriesHelpersRef = useRef(deliveriesHelpers)
    deliveriesHelpersRef.current = deliveriesHelpers

    const deliveriesRef = useRef(deliveries)
    deliveriesRef.current = deliveries

    const setDeliveries = useCallback((updater: (current: DeliveryTimesField) => DeliveryTimesField) => {
        const current = deliveriesRef.current
        const next = updater(current)
        if (!isEqual(current, next)) {
            deliveriesHelpersRef.current.setValue(next)
        }
    }, [])

    const servicesMap = useMemo(() => {
        const map = new Map<number, ExtraServiceDetails>()
        for (const service of services) {
            map.set(service.id, service)
        }
        return map
    }, [services])

    const prevServiceIdsRef = useRef<Set<number>>(new Set(services.map((s) => s.id)))

    // When the available services change (e.g. delivery type changed), remove stale extra service order lines
    useEffect(() => {
        const currentIds = new Set(services.map((s) => s.id))
        const prevIds = prevServiceIdsRef.current
        prevServiceIdsRef.current = currentIds

        // Skip on first render
        if (prevIds.size === 0 && currentIds.size === 0) return

        const hasChanged = prevIds.size !== currentIds.size || Array.from(prevIds).some((id) => !currentIds.has(id))
        if (!hasChanged) return

        setDeliveries((current) => {
            if (!Array.isArray(current?.deliveryTimes)) return current
            return {
                ...current,
                deliveryTimes: current.deliveryTimes.map((delivery, idx) => {
                    if (idx !== deliveryIndex) return delivery
                    return {
                        ...delivery,
                        orderLines: delivery.orderLines.filter(
                            (line) => !line.isExtraService || currentIds.has(line.id as number),
                        ),
                    }
                }),
            }
        })
    }, [services, setDeliveries, deliveryIndex])

    useEffect(() => {
        if (!seatCount) return

        setDeliveries((current) => {
            if (!Array.isArray(current?.deliveryTimes)) return current
            return {
                ...current,
                deliveryTimes: current.deliveryTimes.map((delivery, idx) => {
                    if (idx !== deliveryIndex) return delivery
                    return {
                        ...delivery,
                        orderLines: delivery.orderLines.map((orderLine) => {
                            const service = typeof orderLine.id === "number" && servicesMap.get(orderLine.id)
                            if (!service || !orderLine.isExtraService) return orderLine

                            const updatedPrice = calculateServicePrice(service, seatCount)
                            return {
                                ...orderLine,
                                price: updatedPrice,
                                adjustedPrice: updatedPrice,
                            }
                        }),
                    }
                }),
            }
        })
    }, [seatCount, servicesMap, setDeliveries, deliveryIndex])

    const handleAddService = (service: ExtraServiceDetails) => {
        const totalPrice = calculateServicePrice(service, seatCount)

        const newOrderLine: ProductWithQuantity = {
            id: service.id,
            name: service.name,
            price: totalPrice,
            isActive: service.isActive ?? true,
            quantity: 1,
            isExtraService: true,
            ...(service.isMeetingTimeRequired ? { isMeetingTimeRequired: true } : {}),
            ...(service.isMeetingLocationRequired ? { isMeetingLocationRequired: true } : {}),
        }

        const updatedDeliveries = deliveries.deliveryTimes.map((delivery, idx) => {
            if (idx !== deliveryIndex) return delivery
            if (isServiceAdded(delivery, service.id)) return delivery

            return {
                ...delivery,
                orderLines: [...delivery.orderLines, newOrderLine],
            }
        })

        deliveriesHelpers.setValue({
            ...deliveries,
            deliveryTimes: updatedDeliveries,
        })
    }

    const handleRemoveService = (serviceId: number) => {
        const updatedDeliveries = deliveries.deliveryTimes.map((delivery, idx) => {
            if (idx !== deliveryIndex) return delivery

            return {
                ...delivery,
                orderLines: delivery.orderLines.filter(
                    (p) => !(Number(p.id) === Number(serviceId) && p.isExtraService),
                ),
            }
        })

        deliveriesHelpers.setValue({
            ...deliveries,
            deliveryTimes: updatedDeliveries,
        })
    }

    const delivery = deliveries.deliveryTimes[deliveryIndex]
    if (!delivery) return null

    return (
        <Stack gap={2} className={classes.wrap}>
            <LocalizedStrict
                id="create-order-extra-services-delivery"
                attrs={{ title: true }}
                vars={{ time: deliveryTime }}
            >
                <ExtraServicesAccordion title={`Delivery at ${deliveryTime}`}>
                    {services.map((service) => {
                        const { id, name, description, pricingType } = service
                        const total = calculateServicePrice(service, seatCount)
                        const isAlreadyAdded = isServiceAdded(delivery, id)
                        const isPerSeat = pricingType === "PER_SEAT"

                        return (
                            <Stack gap=".75rem" key={id}>
                                <Stack gap=".25rem">
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        gap="1rem"
                                    >
                                        <Stack flexDirection="row" gap=".5rem" alignItems="center">
                                            <Typography fontWeight="bold">{name}</Typography>
                                            <Tooltip title={description}>
                                                <FontAwesomeIcon icon={faCircleInfo} className={classes.icon} />
                                            </Tooltip>
                                        </Stack>
                                        <Typography variant="subtitle1">
                                            {moneyFactory.format(toDinero(total))}
                                        </Typography>
                                    </Stack>

                                    {isPerSeat && service.minimumPrice && service.maximumPrice && (
                                        <LocalizedStrict
                                            id="create-order-extra-services-min-max"
                                            vars={{
                                                min: moneyFactory.format(toDinero(service.minimumPrice)),
                                                max: moneyFactory.format(toDinero(service.maximumPrice)),
                                            }}
                                        >
                                            <Typography variant="body2" color="text.secondary">
                                                Min. {moneyFactory.format(toDinero(service.minimumPrice))} - Max.
                                                {moneyFactory.format(toDinero(service.maximumPrice))}
                                            </Typography>
                                        </LocalizedStrict>
                                    )}
                                </Stack>

                                <Stack flexDirection="row" gap="1rem" justifyContent="flex-end" alignItems="center">
                                    {isPerSeat && service.pricePerSeat && (
                                        <Typography variant="subtitle2">
                                            {seatCount} seats x {moneyFactory.format(toDinero(service.pricePerSeat))}
                                        </Typography>
                                    )}
                                    {isAlreadyAdded ? (
                                        <LocalizedStrict id="create-order-extra-services-remove-button">
                                            <StandardButton
                                                size="small"
                                                fullWidth={false}
                                                variant="outlined"
                                                color="error"
                                                onClick={() => handleRemoveService(id)}
                                                disabled={isRefundOrder}
                                            >
                                                Remove
                                            </StandardButton>
                                        </LocalizedStrict>
                                    ) : (
                                        <LocalizedStrict id="create-order-extra-services-add-button">
                                            <StandardButton
                                                size="small"
                                                fullWidth={false}
                                                onClick={() => handleAddService(service)}
                                                disabled={isRefundOrder}
                                            >
                                                Add
                                            </StandardButton>
                                        </LocalizedStrict>
                                    )}
                                </Stack>
                            </Stack>
                        )
                    })}
                </ExtraServicesAccordion>
            </LocalizedStrict>
        </Stack>
    )
}
